// Unified client-side API layer
// Replaces all fetch('/api/...') calls with local service calls
// This is the main integration point for the WASM/static hosting migration

import { BrowserGitService } from "./git-service";
import { BrowserJiraService, validateJiraCredentials } from "./jira-service";
import { proxiedFetch, jiraFetch, buildBasicAuth } from "./cors-proxy";
import { atlassianOAuth2 } from "./atlassian-oauth2";
import * as storage from "./storage";
import * as indicatorRunner from "./indicator-runner";
import { getAllRepos, EXAMPLE_REPOS } from "./config";
import {
  generatePDFReport,
  generateIndividualPDFReport,
  generateCrossProjectPDFReport,
  generateCombinedPDFReport,
  generateJiraPDFReport,
  generateConfluencePDFReport,
  downloadPDF,
} from "./pdf-report";
import type {
  RepoConfig,
  Commit,
  RepoStats,
  JiraIntegrationSettings,
  BitbucketRepo,
  GitHubRepo,
  GitLabRepo,
} from "./types";

// ==================== Initialization ====================

let initialized = false;

export async function initAPI(): Promise<void> {
  if (initialized) return;
  await indicatorRunner.initIndicators();
  initialized = true;
  console.log("[API] Client-side API layer initialized");
}

// ==================== Health Check ====================

export async function checkHealth(): Promise<{ status: string; mode: string }> {
  return { status: "ok", mode: "static" };
}

// ==================== Repos ====================

export async function getRepos(): Promise<{
  predefined: Array<{ id: string; name: string; description?: string; type: string; icon?: string; color?: string }>;
  hasYamlConfig: boolean;
}> {
  const repos = await getAllRepos();
  return {
    predefined: repos.map((r) => ({
      id: r.id,
      name: r.name,
      description: r.description,
      type: r.type,
      icon: r.icon,
      color: r.color,
    })),
    hasYamlConfig: false,
  };
}

// ==================== Analysis ====================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ProgressCallback = (line: string) => void;

export const CHANGELOG_CANDIDATES = [
  'CHANGELOG.md', 'CHANGELOG.txt', 'HISTORY.md',
  'RELEASES.md', 'CHANGES.md', 'NEWS.md',
] as const;

export async function analyzeRepoForChangelog(
  config: RepoConfig,
  onProgress?: ProgressCallback,
  startDate?: string,
  endDate?: string
): Promise<{ commits: Commit[]; changelogContent: string | null; changelogFilename: string | null }> {
  await initAPI();
  const gitService = new BrowserGitService({ config, progressCallback: onProgress });
  await gitService.init();
  const weeks = startDate && endDate
    ? Math.max(1, Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24 * 7)))
    : 104;
  const commits = await gitService.getCommits(weeks, startDate, endDate);

  // Search for changelog files in the cloned WASM filesystem
  let changelogContent: string | null = null;
  let changelogFilename: string | null = null;
  for (const candidate of CHANGELOG_CANDIDATES) {
    const content = await gitService.readFile(candidate);
    if (content) { changelogContent = content; changelogFilename = candidate; break; }
  }

  return { commits, changelogContent, changelogFilename };
}

export async function analyzeRepo(
  config: RepoConfig & { weeks?: number; startDate?: string; endDate?: string; sessionId?: string },
  onProgress?: ProgressCallback
): Promise<{
  commits: Commit[];
  stats: RepoStats;
  repoName: string;
  repoType: string;
}> {
  await initAPI();

  const gitService = new BrowserGitService({
    config,
    progressCallback: onProgress,
  });

  // Clone or fetch
  await gitService.init();

  // Get commits
  const commits = await gitService.getCommits(
    config.weeks || 4,
    config.startDate,
    config.endDate
  );

  // Get stats
  const stats = gitService.getStats(
    config.weeks || 4,
    commits,
    config.startDate,
    config.endDate
  );

  // Get branches
  const branches = await gitService.getBranches();
  stats.branches = branches;
  stats.totalBranches = branches.length;

  const repoName = config.name || config.url || config.path || "Unknown Repo";
  const repoType = config.type || "github";

  // Log to audit with rich snapshot data
  const topAuthors = (stats.authors || []).map((a: any) => ({
    name: a.name,
    email: a.email,
    commits: a.commits,
    additions: a.additions,
    deletions: a.deletions,
    activeDays: a.activeDays || 0,
  }));
  // Compute branch commit counts from actual commit data (more reliable than BFS count
  // which can fail due to WASM reinit issues in handleGetBranches)
  const branchCommitCounts = new Map<string, number>();
  for (const c of commits) {
    if (c.branch) {
      branchCommitCounts.set(c.branch, (branchCommitCounts.get(c.branch) || 0) + 1);
    }
  }
  const topBranches = (branches || []).map((b: any) => ({
    name: b.name,
    isRemote: !!b.isRemote,
    commitCount: branchCommitCounts.get(b.name) || b.commitCount || 0,
    lastAuthor: b.lastAuthor,
  }));
  await storage.addToHistory({
    id: `analysis-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    repoName,
    repoType,
    repoUrl: config.url,
    repoPath: config.path,
    dateRange: stats.dateRange,
    stats: {
      totalCommits: stats.totalCommits,
      totalAuthors: stats.totalAuthors,
      totalBranches: stats.totalBranches,
      totalAdditions: stats.totalAdditions,
      totalDeletions: stats.totalDeletions,
    },
    topAuthors,
    topBranches,
    duration: 0,
    success: true,
  });

  return { commits, stats, repoName, repoType };
}

// Multi-repo analysis
export async function analyzeMultiRepo(
  configs: (RepoConfig & { weeks?: number; startDate?: string; endDate?: string })[],
  _sessionId?: string,
  onProgress?: ProgressCallback
): Promise<{
  results: {
    repoId: string;
    repoName: string;
    repoType: string;
    commits: Commit[];
    stats: RepoStats;
    error?: string;
  }[];
}> {
  await initAPI();

  const results = [];

  for (let i = 0; i < configs.length; i++) {
    const config = configs[i];
    const repoName = config.name || config.url || config.path || `Repo ${i + 1}`;

    if (onProgress) {
      onProgress(`[${i + 1}/${configs.length}] Analyzing ${repoName}...`);
    }

    try {
      const result = await analyzeRepo(config, onProgress);
      results.push({
        repoId: (config as any).repoId || `repo-${i}`,
        repoName: result.repoName,
        repoType: result.repoType,
        commits: result.commits,
        stats: result.stats,
      });
    } catch (e: unknown) {
      const err = e as Error;
      results.push({
        repoId: (config as any).repoId || `repo-${i}`,
        repoName,
        repoType: config.type || "github",
        commits: [],
        stats: {
          totalCommits: 0,
          totalAuthors: 0,
          totalBranches: 0,
          totalAdditions: 0,
          totalDeletions: 0,
          authors: [],
          dailyActivity: [],
          branches: [],
          dateRange: { start: "", end: "" },
        },
        error: err.message,
      });
    }
  }

  return { results };
}

// ==================== Diff ====================

export async function getCommitDiff(
  config: RepoConfig,
  hash: string
): Promise<{ diff: string }> {
  const gitService = new BrowserGitService({ config });
  const diff = await gitService.getCommitDiff(hash);
  return { diff };
}

// ==================== Audit Log ====================

export async function getHistory() {
  return storage.getHistory();
}

export async function clearHistory() {
  return storage.clearHistory();
}

export async function getAuditLog() {
  return storage.getAuditLog();
}

// ==================== Logs ====================

// In static mode, logs are in-memory
const logLines: string[] = [];

export function addLog(line: string): void {
  logLines.push(line);
  if (logLines.length > 1000) logLines.shift();
}

export function getLogs(): { logs: string } {
  return { logs: logLines.join("\n") };
}

// ==================== Live Watcher ====================

// In static mode, live watching uses polling via wasm-git
const watchedRepos = new Map<string, {
  config: RepoConfig;
  lastHash: string | null;
  interval: ReturnType<typeof setInterval> | null;
}>();

export function startWatch(repoId: string, config: RepoConfig): void {
  if (watchedRepos.has(repoId)) return;
  watchedRepos.set(repoId, { config, lastHash: null, interval: null });
}

export function stopWatch(repoId: string): void {
  const watcher = watchedRepos.get(repoId);
  if (watcher?.interval) {
    clearInterval(watcher.interval);
  }
  watchedRepos.delete(repoId);
}

export async function checkWatch(_repoId: string): Promise<{
  hasNew: boolean;
  commits: Commit[];
}> {
  // For now, return empty — live watching in static mode
  // would require periodic git.fetch which we can implement later
  return { hasNew: false, commits: [] };
}

// ==================== Performance Targets ====================

export async function getTargets() {
  return storage.getTargets();
}

export async function getTargetsByAuthor(authorName: string) {
  return storage.getTargetsByAuthor(authorName);
}

export async function createTarget(
  target: Parameters<typeof storage.createTarget>[0]
) {
  return storage.createTarget(target);
}

export async function updateTarget(
  id: string,
  updates: Parameters<typeof storage.updateTarget>[1]
) {
  return storage.updateTarget(id, updates);
}

export async function deleteTarget(id: string) {
  return storage.deleteTarget(id);
}

// ==================== Bitbucket ====================

export async function getBitbucketRepos(
  username: string,
  appPassword: string,
  workspace?: string
): Promise<{ repos: BitbucketRepo[] }> {
  const auth = buildBasicAuth(username, appPassword);
  const repos: BitbucketRepo[] = [];

  // Use /2.0/repositories?role=member to list all repos the authenticated user can access.
  // Only scope to a specific workspace if explicitly provided.
  let url = workspace
    ? `https://api.bitbucket.org/2.0/repositories/${workspace}?pagelen=100&role=member`
    : `https://api.bitbucket.org/2.0/repositories?pagelen=100&role=member`;

  try {
    while (url) {
      const response = await proxiedFetch(url, {
        headers: { Authorization: auth, Accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Bitbucket API error ${response.status}`);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: any = await response.json();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const repo of data.values || []) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cloneLink = (repo.links?.clone || []).find((l: any) => l.name === "https");
        repos.push({
          slug: repo.slug,
          name: repo.name,
          fullName: repo.full_name,
          description: repo.description || "",
          isPrivate: repo.is_private,
          project: repo.project?.name || null,
          projectKey: repo.project?.key || null,
          workspace: repo.workspace?.slug || workspace || username,
          url: repo.links?.html?.href || "",
          cloneUrl: cloneLink?.href || "",
          updatedOn: repo.updated_on,
          language: repo.language || null,
          size: repo.size || 0,
        });
      }

      url = data.next || "";
    }
  } catch (e: unknown) {
    const err = e as Error;
    console.error("[Bitbucket] Failed to fetch repos:", err.message);
  }

  return { repos };
}

export async function validateBitbucketAuth(
  username: string,
  appPassword: string
): Promise<{ valid: boolean; error?: string }> {
  try {
    const auth = buildBasicAuth(username, appPassword);
    const response = await proxiedFetch(
      `https://api.bitbucket.org/2.0/user`,
      { headers: { Authorization: auth, Accept: "application/json" } }
    );

    if (response.ok) {
      return { valid: true };
    }
    return { valid: false, error: `HTTP ${response.status}` };
  } catch (e: unknown) {
    const err = e as Error;
    return { valid: false, error: err.message };
  }
}

// ==================== Jira ====================

/** Returns the current Atlassian OAuth2 credentials {oauthToken, cloudId} if available, else {}.
 *  cloudId is required to build the api.atlassian.com/ex/jira/{cloudId} base URL.
 */
async function resolveJiraOAuth(): Promise<{ oauthToken?: string; cloudId?: string }> {
  try {
    const [token, state] = await Promise.all([
      atlassianOAuth2.getAccessToken(),
      atlassianOAuth2.getState(),
    ]);
    if (!token) return {};
    return { oauthToken: token, cloudId: state.token?.cloudId };
  } catch {
    return {};
  }
}

export async function getJiraSettings(): Promise<JiraIntegrationSettings | null> {
  return storage.getJiraSettings();
}

export async function getConfluenceSettings(): Promise<import('./types').ConfluenceSettings | null> {
  return storage.getConfluenceSettings();
}

export async function saveJiraSettings(settings: JiraIntegrationSettings): Promise<void> {
  return storage.saveJiraSettings(settings);
}

export async function clearJiraSettings(): Promise<void> {
  return storage.clearJiraSettings();
}

export async function validateJira(
  domain: string,
  email: string,
  apiToken: string
) {
  return validateJiraCredentials({ domain, email, apiToken });
}

export async function getJiraBoards(
  domain: string,
  email: string,
  apiToken: string
) {
  const oauth = await resolveJiraOAuth();
  const svc = new BrowserJiraService({ domain, email, apiToken, ...oauth });
  return svc.getBoards();
}

export async function getJiraProjects(
  domain: string,
  email: string,
  apiToken: string
) {
  const oauth = await resolveJiraOAuth();
  const svc = new BrowserJiraService({ domain, email, apiToken, ...oauth });
  return svc.getProjects();
}

export async function getJiraProjectUsers(
  domain: string,
  email: string,
  apiToken: string,
  projectKey: string
) {
  const oauth = await resolveJiraOAuth();
  const svc = new BrowserJiraService({ domain, email, apiToken, ...oauth });
  return svc.getProjectUsers(projectKey);
}

export async function getJiraLagIndicators(
  domain: string,
  email: string,
  apiToken: string,
  projectKey: string,
  developers: string[] = [],
  lookbackDays: number = 90,
  startDate?: string,
  endDate?: string
) {
  const oauth = await resolveJiraOAuth();
  const svc = new BrowserJiraService({ domain, email, apiToken, ...oauth });
  const indicators = await svc.calculateLagIndicators(
    projectKey,
    developers,
    lookbackDays,
    startDate,
    endDate
  );
  return { indicators };
}

export async function getJiraLeadIndicators(
  domain: string,
  email: string,
  apiToken: string,
  projectKey: string,
  developers: string[] = [],
  lookbackDays: number = 90,
  startDate?: string,
  endDate?: string
) {
  const oauth = await resolveJiraOAuth();
  const svc = new BrowserJiraService({ domain, email, apiToken, ...oauth });
  const indicators = await svc.calculateLeadIndicators(
    projectKey,
    developers,
    lookbackDays,
    startDate,
    endDate
  );
  return { indicators };
}

export async function getJiraActivity(
  domain: string,
  email: string,
  apiToken: string,
  projectKey: string,
  lookbackDays: number = 90,
  boardId?: number,
  startDate?: string,
  endDate?: string
) {
  const oauth = await resolveJiraOAuth();
  const svc = new BrowserJiraService({ domain, email, apiToken, ...oauth });
  return svc.getActivityFeed(projectKey, lookbackDays, boardId, startDate, endDate);
}

export async function getJiraIssueDetail(
  domain: string,
  email: string,
  apiToken: string,
  issueKey: string
) {
  const oauth = await resolveJiraOAuth();
  const svc = new BrowserJiraService({ domain, email, apiToken, ...oauth });
  return svc.getIssueDetail(issueKey);
}

export async function getJiraProjectSummary(
  domain: string,
  email: string,
  apiToken: string,
  projectKey: string,
  boardId?: number
) {
  const oauth = await resolveJiraOAuth();
  const svc = new BrowserJiraService({ domain, email, apiToken, ...oauth });
  return svc.getProjectSummary(projectKey, boardId);
}

export async function searchJiraIssues(
  domain: string,
  email: string,
  apiToken: string,
  jql: string
) {
  const oauth = await resolveJiraOAuth();
  const svc = new BrowserJiraService({ domain, email, apiToken, ...oauth });
  return svc.searchIssues(jql);
}

// Jira attachment — direct fetch in hosted mode, dev proxy on localhost
export async function getJiraAttachment(
  _domain: string,
  email: string,
  apiToken: string,
  attachmentUrl: string
): Promise<Blob> {
  const { oauthToken } = await resolveJiraOAuth();
  const auth = oauthToken ? `Bearer ${oauthToken}` : buildBasicAuth(email, apiToken);
  const response = await jiraFetch(attachmentUrl, {
    headers: { Authorization: auth, Accept: '*/*' },
  });
  return response.blob();
}

// ==================== Indicators ====================

export async function getIndicators() {
  await initAPI();
  const configs = indicatorRunner.getAllIndicatorConfigs();
  const custom = indicatorRunner.getCustomIndicators();
  const disabled = indicatorRunner.getDisabledIndicators();
  const indicators = configs.map((c) => ({
    id: c.id,
    name: c.name,
    description: c.description,
    type: c.type,
    needsDevInfo: c.needsDevInfo,
    scope: c.scope || 'per-user',
    source: c.source || 'jira',
    enabled: indicatorRunner.isIndicatorEnabled(c.id),
    isCustom: indicatorRunner.isUserCreated(c.id),
    isCustomized: custom[c.id] !== undefined,
  }));
  return { indicators, custom, disabled };
}

export async function getIndicatorDetail(id: number) {
  await initAPI();
  const config = indicatorRunner.getIndicatorConfig(id);
  if (!config) return null;
  const effectiveCode = indicatorRunner.getEffectiveCode(id);
  const isCustom = indicatorRunner.isUserCreated(id);
  const enabled = indicatorRunner.isIndicatorEnabled(id);
  return { ...config, effectiveCode, isCustom, enabled };
}

export async function updateIndicatorCode(id: number, code: string) {
  await initAPI();
  await indicatorRunner.setCustomIndicatorCode(id, code);
  return { success: true };
}

export async function resetIndicatorCode(id: number) {
  await initAPI();
  await indicatorRunner.deleteCustomIndicatorCode(id);
  return { success: true };
}

export async function resetAllIndicatorCode() {
  await initAPI();
  await indicatorRunner.resetAllCustomIndicators();
  return { success: true };
}

export async function toggleIndicator(id: number, enabled: boolean) {
  await initAPI();
  await indicatorRunner.setIndicatorEnabled(id, enabled);
  return { success: true };
}

export async function createIndicator(config: {
  name: string;
  description: string;
  type: "lag" | "lead";
  needsDevInfo: boolean;
  scope: "per-user" | "overall";
  source: "jira" | "git" | "both";
  code: string;
}) {
  await initAPI();
  const indicator = await indicatorRunner.createIndicator(config);
  return { indicator };
}

export async function updateIndicatorConfig(id: number, updates: { scope?: "per-user" | "overall"; source?: "jira" | "git" | "both" }) {
  await initAPI();
  const updated = await indicatorRunner.updateIndicatorConfig(id, updates);
  return { updated };
}

export async function deleteIndicator(id: number) {
  await initAPI();
  const deleted = await indicatorRunner.deleteIndicator(id);
  return { deleted };
}

export async function validateIndicatorCode(code: string) {
  return indicatorRunner.validateIndicatorCode(code);
}

// ==================== PDF Reports ====================

function buildReportMeta(type: string, data: Record<string, unknown>): { title: string; description: string; metadata: Record<string, unknown> } {
  switch (type) {
    case "main":
      return {
        title: `${(data.repoName as string) || 'Repository'} Report`,
        description: `Git analysis — ${(data.stats as Record<string, unknown>)?.totalCommits || 0} commits, ${(data.stats as Record<string, unknown>)?.totalAuthors || 0} authors`,
        metadata: { repoName: data.repoName, repoType: data.repoType, dateRange: data.dateRange, totalCommits: (data.stats as Record<string, unknown>)?.totalCommits, totalAuthors: (data.stats as Record<string, unknown>)?.totalAuthors },
      };
    case "individual":
      return {
        title: `Individual Report — ${(data.author as Record<string, unknown>)?.name || 'Unknown'}`,
        description: `${(data.repoName as string) || 'Repository'} — ${(data.author as Record<string, unknown>)?.commits || 0} commits`,
        metadata: { repoName: data.repoName, authorName: (data.author as Record<string, unknown>)?.name, commits: (data.author as Record<string, unknown>)?.commits },
      };
    case "cross-project":
      return {
        title: 'Cross-Project Report',
        description: `${(data.repos as unknown[])?.length || 0} repositories analysed`,
        metadata: { repoCount: (data.repos as unknown[])?.length, dateRange: data.dateRange },
      };
    case "combined":
      return {
        title: `Combined Report — ${(data.repoName as string) || 'Repository'}`,
        description: `Git + Jira combined analysis`,
        metadata: { repoName: data.repoName, jiraProject: data.jiraProjectKey, dateRange: data.dateRange },
      };
    case "jira":
      return {
        title: `Jira Report — ${(data.projectKey as string) || 'Project'}`,
        description: `${(data.summary as Record<string, unknown>)?.totalEvents || 0} events analysed`,
        metadata: { projectKey: data.projectKey, dateRange: data.dateRange, eventCount: (data.summary as Record<string, unknown>)?.totalEvents },
      };
    case "confluence":
      return {
        title: `Confluence Report — ${(data.spaceName as string) || (data.spaceKey as string) || 'Space'}`,
        description: `${data.totalPages || 0} pages, ${data.uniqueContributors || 0} contributors`,
        metadata: { spaceKey: data.spaceKey, spaceName: data.spaceName, dateRange: data.dateRange, totalPages: data.totalPages },
      };
    default:
      return { title: 'Report', description: '', metadata: {} };
  }
}

export function generateAndDownloadReport(
  type: "main" | "individual" | "cross-project" | "combined" | "jira" | "confluence",
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any,
  filename?: string
): void {
  let pdfData: Uint8Array;
  let resolvedFilename: string;

  switch (type) {
    case "main":
      pdfData = generatePDFReport(data);
      resolvedFilename = filename || `verigit-report-${Date.now()}.pdf`;
      break;
    case "individual":
      pdfData = generateIndividualPDFReport(data);
      resolvedFilename = filename || `verigit-individual-${data.author?.name || "report"}-${Date.now()}.pdf`;
      break;
    case "cross-project":
      pdfData = generateCrossProjectPDFReport(data);
      resolvedFilename = filename || `verigit-cross-project-${Date.now()}.pdf`;
      break;
    case "combined":
      pdfData = generateCombinedPDFReport(data);
      resolvedFilename = filename || `verigit-combined-${Date.now()}.pdf`;
      break;
    case "jira":
      pdfData = generateJiraPDFReport(data);
      resolvedFilename = filename || `verigit-jira-${Date.now()}.pdf`;
      break;
    case "confluence":
      pdfData = generateConfluencePDFReport(data);
      resolvedFilename = filename || `verigit-confluence-${(data.spaceKey || 'space').toLowerCase().replace(/[^a-z0-9]/g, '-')}-${Date.now()}.pdf`;
      break;
  }

  downloadPDF(pdfData!, resolvedFilename!);

  // Save a copy to IndexedDB for the Reports page (fire-and-forget)
  const meta = buildReportMeta(type, data);
  storage.saveReport({
    id: `report-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    type,
    filename: resolvedFilename!,
    title: meta.title,
    description: meta.description,
    sizeBytes: pdfData!.byteLength,
    pdfBlob: pdfData!,
    metadata: meta.metadata,
  }).catch(err => console.warn('[Reports] Failed to save report copy:', err));
}

// ==================== Settings ====================

export async function getCorsProxyConfig() {
  return storage.getCorsProxyConfig();
}

export async function setCorsProxyConfig(config: { enabled: boolean; url: string }) {
  return storage.setCorsProxyConfig(config);
}

// ==================== Auth ====================

export async function validateAuth(email: string, token: string): Promise<{ valid: boolean; error?: string; username?: string; displayName?: string; avatarUrl?: string }> {
  try {
    // Validate Bitbucket credentials via the same-origin CORS proxy
    const res = await proxiedFetch('https://api.bitbucket.org/2.0/user', {
      headers: {
        'Authorization': 'Basic ' + btoa(`${email}:${token}`),
        'Accept': 'application/json',
      },
    });
    
    if (res.ok) {
      const data = await res.json();
      return {
        valid: true,
        username: data.username,
        displayName: data.display_name,
        avatarUrl: data.links?.avatar?.href,
      };
    } else {
      return { valid: false, error: `Authentication failed (${res.status})` };
    }
  } catch (err: any) {
    return { valid: false, error: err.message || 'Network error' };
  }
}

export async function validateGitHubAuth(token: string): Promise<{ valid: boolean; error?: string; username?: string; displayName?: string; avatarUrl?: string }> {
  try {
    const res = await proxiedFetch('https://api.github.com/user', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/vnd.github+json',
        'X-GitHub-Api-Version': '2022-11-28',
      },
    });

    if (res.ok) {
      const data = await res.json();
      return {
        valid: true,
        username: data.login,
        displayName: data.name || data.login,
        avatarUrl: data.avatar_url,
      };
    } else {
      return { valid: false, error: `GitHub authentication failed (${res.status})` };
    }
  } catch (err: any) {
    return { valid: false, error: err.message || 'Network error' };
  }
}

// ==================== GitHub Repos ====================

export async function getGitHubRepos(
  token: string
): Promise<{ repos: GitHubRepo[] }> {
  const repos: GitHubRepo[] = [];
  let page = 1;

  try {
    while (true) {
      const response = await proxiedFetch(
        `https://api.github.com/user/repos?per_page=100&page=${page}&sort=updated&affiliation=owner,collaborator,organization_member`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/vnd.github+json',
            'X-GitHub-Api-Version': '2022-11-28',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`GitHub API error ${response.status}`);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: any[] = await response.json();
      if (data.length === 0) break;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const repo of data) {
        repos.push({
          id: repo.id,
          name: repo.name,
          fullName: repo.full_name,
          description: repo.description || '',
          isPrivate: repo.private,
          owner: repo.owner?.login || '',
          url: repo.html_url || '',
          cloneUrl: repo.clone_url || '',
          updatedAt: repo.updated_at,
          language: repo.language || null,
          size: repo.size || 0,
          defaultBranch: repo.default_branch || 'main',
          stargazersCount: repo.stargazers_count || 0,
          forksCount: repo.forks_count || 0,
        });
      }

      if (data.length < 100) break;
      page++;
    }
  } catch (e: unknown) {
    const err = e as Error;
    console.error('[GitHub] Failed to fetch repos:', err.message);
  }

  return { repos };
}

// ==================== Legacy Git Credential Migration ====================

/**
 * One-time silent migration: reads Bitbucket/GitHub credentials from the old
 * `verigit-auth` localStorage key (and `verigit-saved-profiles`) and saves
 * them to the new IndexedDB stores if they haven't been saved yet.
 * Safe to call repeatedly — skips if credentials already exist in IndexedDB.
 */
export async function migrateLegacyGitCredentials(): Promise<void> {
  try {
    // Try verigit-auth first
    let bbEmail = '', bbToken = '', bbUsername = '', ghToken = '', ghUsername = '';

    const legacyRaw = localStorage.getItem('verigit-auth');
    if (legacyRaw) {
      const p = JSON.parse(legacyRaw);
      bbEmail = p.email || '';
      bbToken = p.token || '';
      bbUsername = p.username || p.bitbucketUsername || '';
      ghToken = p.githubToken || '';
      ghUsername = p.githubUsername || '';
    }

    // Also try verigit-saved-profiles (may have more complete data)
    if (!bbToken && !ghToken) {
      const profilesRaw = localStorage.getItem('verigit-saved-profiles');
      if (profilesRaw) {
        const profiles: any[] = JSON.parse(profilesRaw);
        const first = profiles[0];
        if (first) {
          bbEmail = bbEmail || first.email || '';
          bbToken = bbToken || first.token || '';
          bbUsername = bbUsername || first.username || '';
          ghToken = ghToken || first.githubToken || '';
          ghUsername = ghUsername || first.githubUsername || '';
        }
      }
    }

    // Save Bitbucket if we have credentials and they're not already in IndexedDB
    if (bbEmail && bbToken) {
      const existing = await storage.getBitbucketSettings().catch(() => null);
      if (!existing?.token) {
        await storage.saveBitbucketSettings({ email: bbEmail, token: bbToken, username: bbUsername || undefined });
        console.log('[migration] Migrated Bitbucket credentials to IndexedDB');
      }
    }

    // Save GitHub if we have credentials and they're not already in IndexedDB
    if (ghToken) {
      const existing = await storage.getGitHubSettings().catch(() => null);
      if (!existing?.token) {
        await storage.saveGitHubSettings({ token: ghToken, username: ghUsername || '' });
        console.log('[migration] Migrated GitHub credentials to IndexedDB');
      }
    }
  } catch (e) {
    console.warn('[migration] Legacy git credential migration failed silently:', e);
  }
}

// ==================== Bitbucket Settings ====================

export async function getBitbucketSettings() {
  return storage.getBitbucketSettings();
}

export async function saveBitbucketSettings(settings: { email: string; token: string; username?: string }) {
  return storage.saveBitbucketSettings(settings);
}

export async function clearBitbucketSettings() {
  return storage.clearBitbucketSettings();
}

// ==================== GitHub Settings ====================

export async function getGitHubSettings() {
  return storage.getGitHubSettings();
}

export async function saveGitHubSettings(settings: { username: string; token: string }) {
  return storage.saveGitHubSettings(settings);
}

export async function clearGitHubSettings() {
  return storage.clearGitHubSettings();
}

// ==================== Linear Settings ====================

export async function getLinearSettings() {
  return storage.getLinearSettings();
}

export async function saveLinearSettings(settings: import('./types').LinearIntegrationSettings) {
  return storage.saveLinearSettings(settings);
}

export async function clearLinearSettings() {
  return storage.clearLinearSettings();
}

// ==================== Monday Settings ====================

export async function getMondaySettings() {
  return storage.getMondaySettings();
}

export async function saveMondaySettings(settings: import('./types').MondayIntegrationSettings) {
  return storage.saveMondaySettings(settings);
}

export async function clearMondaySettings() {
  return storage.clearMondaySettings();
}

// ==================== Trello Settings ====================

export async function getTrelloSettings() {
  return storage.getTrelloSettings();
}

export async function saveTrelloSettings(settings: import('./types').TrelloIntegrationSettings) {
  return storage.saveTrelloSettings(settings);
}

export async function clearTrelloSettings() {
  return storage.clearTrelloSettings();
}

// ==================== GitLab Repos ====================

export async function getGitLabRepos(
  token: string,
  instanceUrl: string = 'https://gitlab.com'
): Promise<{ repos: GitLabRepo[] }> {
  const repos: GitLabRepo[] = [];
  let page = 1;
  const baseUrl = instanceUrl.replace(/\/+$/, '');

  try {
    while (true) {
      const response = await proxiedFetch(
        `${baseUrl}/api/v4/projects?membership=true&per_page=100&page=${page}&order_by=updated_at&sort=desc`,
        {
          headers: {
            'PRIVATE-TOKEN': token,
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`GitLab API error ${response.status}`);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: any[] = await response.json();
      if (data.length === 0) break;

      for (const project of data) {
        repos.push({
          id: project.id,
          name: project.name,
          fullName: project.path_with_namespace,
          description: project.description || '',
          isPrivate: project.visibility === 'private',
          owner: project.namespace?.full_path || '',
          url: project.web_url || '',
          cloneUrl: project.http_url_to_repo || '',
          sshUrl: project.ssh_url_to_repo || '',
          updatedAt: project.last_activity_at || project.updated_at || '',
          language: null, // GitLab doesn't include this in project list
          defaultBranch: project.default_branch || 'main',
          starCount: project.star_count || 0,
          forksCount: project.forks_count || 0,
          namespace: project.namespace?.name || '',
          avatarUrl: project.avatar_url || null,
        });
      }

      if (data.length < 100) break;
      page++;
    }
  } catch (e: unknown) {
    const err = e as Error;
    console.error('[GitLab] Failed to fetch repos:', err.message);
  }

  return { repos };
}

export async function validateGitLabAuth(
  token: string,
  instanceUrl: string = 'https://gitlab.com'
): Promise<{ valid: boolean; username?: string; error?: string }> {
  const baseUrl = instanceUrl.replace(/\/+$/, '');
  try {
    const response = await proxiedFetch(`${baseUrl}/api/v4/user`, {
      headers: { 'PRIVATE-TOKEN': token, Accept: 'application/json' },
    });
    if (!response.ok) {
      return { valid: false, error: `HTTP ${response.status}` };
    }
    const data = await response.json();
    return { valid: true, username: data.username };
  } catch (e: unknown) {
    return { valid: false, error: (e as Error).message };
  }
}

// ==================== GitLab Settings ====================

export async function getGitLabSettings() {
  return storage.getGitLabSettings();
}

export async function saveGitLabSettings(settings: import('./types').GitLabSettings) {
  return storage.saveGitLabSettings(settings);
}

export async function clearGitLabSettings() {
  return storage.clearGitLabSettings();
}

// ==================== Notion Settings ====================

export async function getNotionSettings() {
  return storage.getNotionSettings();
}

export async function saveNotionSettings(settings: import('./types').NotionSettings) {
  return storage.saveNotionSettings(settings);
}

export async function clearNotionSettings() {
  return storage.clearNotionSettings();
}

// ==================== Google Docs Settings ====================

export async function getGoogleDocsSettings() {
  return storage.getGoogleDocsSettings();
}

export async function saveGoogleDocsSettings(settings: import('./types').GoogleDocsSettings) {
  return storage.saveGoogleDocsSettings(settings);
}

export async function clearGoogleDocsSettings() {
  return storage.clearGoogleDocsSettings();
}

// ==================== Export all for convenience ====================

export { EXAMPLE_REPOS };
